import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowDown, ArrowUp, CalendarDays, Check, Copy, DoorOpen, ListChecks, Maximize2, Receipt, RotateCcw, Send, Square, ThumbsDown, ThumbsUp, Ticket, X } from "lucide-react";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AssistantCard, followUpsFor } from "@/components/assistant/AssistantCards";
import { RichText } from "@/components/assistant/RichText";
import { errorMessage } from "@/lib/api";
import {
  CHAT_OPEN_EVENT, cancelAssistantDraft, confirmAssistantDraft, fetchAssistantSession, fetchAssistantStatus, getRating, getSessionId,
  markGreeted, resetSession, setRating, streamAssistantChat, wasGreeted,
} from "@/lib/assistant";
import { fmtDate } from "@/lib/format";
import { cn } from "@/lib/utils";

/*
 * «Spør oss» – the house answering questions about rooms, prices, free dates and the programme.
 * It has no name and no mascot: a quiet mark, warm paper, one gold accent. Everything it says is grounded in the
 * house's own data (the cards), and the board always has the last word.
 */

const ease = [0.2, 0.8, 0.2, 1];
export const CHAT_PATH = "/spor-oss";

/** Quick ways in – the questions people actually come with. `text` is what is sent. */
export const QUICK = [
  { icon: CalendarDays, label: "Ledige helger i Stovene", text: "Kva helger er ledige i Stovene framover?" },
  { icon: Receipt, label: "Pris for bryllaup, 80 gjester", text: "Eg planlegg bryllaup for 80 – kva rom passar, og kva kostar det?" },
  { icon: Ticket, label: "Kva skjer framover?", text: "Kva skjer på huset framover?" },
  { icon: DoorOpen, label: "Rom til møte for 30", text: "Kva rom passar til eit møte for 30 personar, og kva kostar det?" },
  { icon: ListChecks, label: "Reglar for vask og rydding", text: "Kva reglar gjeld for vask, rydding og dekking dagen før?" },
  { icon: Send, label: "Send ein førespurnad", text: "Eg vil sende ein førespurnad om å leige eit rom." },
];

/** What an answer was grounded in – short nouns, keyed by tool name from the stream. */
const SOURCE = {
  get_house_info: "huset",
  list_rooms: "romma",
  check_availability: "kalenderen",
  find_free_dates: "kalenderen",
  estimate_price: "prislista",
  list_events: "programmet",
  prepare_booking_request: "førespurnaden",
  ask_contact_details: "skjemaet",
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 5) return "God natt";
  if (h < 10) return "God morgon";
  if (h < 18) return "Hei";
  return "God kveld";
};

const timeOf = (ts) => {
  const d = ts ? new Date(ts) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
const dayLabel = (ts) => {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  const y = new Date(); y.setDate(today.getDate() - 1);
  const same = (a, b) => a.toDateString() === b.toDateString();
  if (same(d, today)) return "I dag";
  if (same(d, y)) return "I går";
  return fmtDate(d.toISOString().slice(0, 10), "EEEE d. MMM");
};
/** A divider is drawn before a message when the day changes or more than 30 minutes have passed. */
const needsDivider = (prev, m) => {
  if (!m?.ts) return false;
  if (!prev?.ts) return true;
  const a = new Date(prev.ts).getTime(); const b = new Date(m.ts).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return false;
  return new Date(a).toDateString() !== new Date(b).toDateString() || b - a > 30 * 60 * 1000;
};

/** The house's mark – amphitheatre rows around one point of light. */
export const ChatMark = ({ className, light = false }) => (
  <span className={cn("flex shrink-0 items-center justify-center overflow-hidden rounded-full", light ? "bg-paper text-ink" : "bg-ink text-paper", className)} aria-hidden="true">
    <svg viewBox="0 0 32 32" className="h-[62%] w-[62%]" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
      <path d="M6 22a10 10 0 0 1 20 0" opacity=".45" />
      <path d="M10 22a6 6 0 0 1 12 0" opacity=".75" />
      <path d="M13.5 22a2.5 2.5 0 0 1 5 0" />
      <circle cx="16" cy="22" r="1.6" fill="hsl(var(--sol))" stroke="none" />
    </svg>
  </span>
);

/* ------------------------------------------------------------------------------ state */
export const useChat = ({ active = true } = {}) => {
  const [sessionId, setSessionId] = useState(() => getSessionId());
  const [messages, setMessages] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [status, setStatus] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [busyDraft, setBusyDraft] = useState(null);
  const [lastFailed, setLastFailed] = useState(null);
  const abortRef = useRef(null);
  const streamingRef = useRef(false);

  useEffect(() => {
    if (!active || loaded) return undefined;
    let cancelled = false;
    (async () => {
      const [s, t] = await Promise.all([fetchAssistantStatus().catch(() => null), fetchAssistantSession(sessionId).catch(() => null)]);
      if (cancelled) return;
      setStatus(s);
      if (t) {
        setMessages((t.transcript || []).map((m) => ({ ...m, cards: m.cards || [] })));
        setDrafts(t.drafts || {});
      }
      setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [active, loaded, sessionId]);

  const send = useCallback(async (text) => {
    const msg = (text || "").trim();
    if (!msg || streamingRef.current) return;
    streamingRef.current = true;
    const now = new Date().toISOString();
    const userMsg = { id: `u-${Date.now()}`, role: "user", content: msg, cards: [], ts: now };
    const pendingId = `a-${Date.now()}`;
    setMessages((m) => [...m, userMsg, { id: pendingId, role: "assistant", content: "", cards: [], pending: true, tools: [], steps: [], ts: now }]);
    setStreaming(true);
    setLastFailed(null);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const patch = (fn) => setMessages((m) => m.map((x) => (x.id === pendingId ? fn(x) : x)));
    try {
      await streamAssistantChat({
        sessionId, message: msg, signal: ctrl.signal,
        onEvent: (ev) => {
          if (ev.type === "delta") patch((x) => ({ ...x, content: x.content + (ev.text || ""), tools: [] }));
          else if (ev.type === "tool_start") patch((x) => ({ ...x, tools: [...(x.tools || []), ev.label || "Hentar …"], steps: x.steps.includes(ev.name) ? x.steps : [...x.steps, ev.name] }));
          else if (ev.type === "card") patch((x) => ({ ...x, cards: [...x.cards, ev.card] }));
          else if (ev.type === "error") patch((x) => ({ ...x, error: true, content: ev.message || x.content }));
          else if (ev.type === "done" && ev.message) patch((x) => ({ ...ev.message, ts: ev.message.ts || x.ts, cards: ev.message.cards?.length ? ev.message.cards : x.cards, steps: x.steps, pending: false, tools: [], error: Boolean(x.error || ev.message.error) }));
        },
      });
      patch((x) => ({ ...x, pending: false, tools: [] }));
    } catch (e) {
      if (e?.name === "AbortError") {
        patch((x) => ({ ...x, pending: false, tools: [], content: x.content || "Stoppa." }));
      } else {
        const text429 = e?.status === 429 ? e.message : null;
        patch((x) => ({ ...x, pending: false, tools: [], error: true, content: text429 || "Vi fekk ikkje kontakt akkurat no. Prøv igjen om litt – eller bruk skjemaet på /leig-rom." }));
        if (!text429) setLastFailed(msg);
      }
    } finally {
      streamingRef.current = false;
      setStreaming(false);
      abortRef.current = null;
    }
  }, [sessionId]);

  const stop = useCallback(() => abortRef.current?.abort(), []);

  const retry = useCallback(() => {
    if (!lastFailed) return;
    setMessages((m) => m.slice(0, -2));
    const text = lastFailed;
    setLastFailed(null);
    setTimeout(() => send(text), 0);
  }, [lastFailed, send]);

  const confirmDraft = useCallback(async (draftId) => {
    if (busyDraft) return;
    setBusyDraft(draftId);
    try {
      const res = await confirmAssistantDraft(sessionId, draftId);
      setDrafts((d) => ({ ...d, [draftId]: { id: draftId, status: "sent", result: { id: res.id } } }));
      if (res.assistant_message && !res.already_sent) setMessages((m) => [...m, { ...res.assistant_message, cards: [], ts: res.assistant_message.ts || new Date().toISOString() }]);
      toast.success("Førespurnaden er sendt til styret", { description: "Ikkje bindande – svar kjem på e-post." });
    } catch (e) {
      if (e?.response?.status === 409) {
        setTimeout(async () => { try { const t = await fetchAssistantSession(sessionId); setDrafts(t.drafts || {}); } catch { /* ignore */ } }, 1200);
      } else {
        toast.error(errorMessage(e, "Fekk ikkje sendt førespurnaden"));
      }
    } finally {
      setBusyDraft(null);
    }
  }, [sessionId, busyDraft]);

  const cancelDraft = useCallback(async (draftId) => {
    if (busyDraft) return;
    setBusyDraft(draftId);
    try {
      await cancelAssistantDraft(sessionId, draftId);
      setDrafts((d) => ({ ...d, [draftId]: { id: draftId, status: "cancelled", result: null } }));
    } catch (e) {
      toast.error(errorMessage(e, "Kunne ikkje avbryte utkastet"));
    } finally {
      setBusyDraft(null);
    }
  }, [sessionId, busyDraft]);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setSessionId(resetSession());
    setMessages([]);
    setDrafts({});
    setLastFailed(null);
  }, []);

  return { sessionId, messages, drafts, status, loaded, streaming, busyDraft, lastFailed, send, stop, retry, confirmDraft, cancelDraft, reset };
};

/* --------------------------------------------------------------------------- pieces */
const IconButton = ({ label, onClick, to, className, children, testId, hiddenOnMobile = false }) => {
  const cls = cn("flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", hiddenOnMobile && "hidden sm:flex", className);
  const inner = to ? <Link to={to} onClick={onClick} className={cls} aria-label={label} data-testid={testId}>{children}</Link> : <button type="button" onClick={onClick} className={cls} aria-label={label} data-testid={testId}>{children}</button>;
  return (
    <Tooltip><TooltipTrigger asChild>{inner}</TooltipTrigger><TooltipContent side="bottom">{label}</TooltipContent></Tooltip>
  );
};

const ToolLine = ({ label }) => (
  <p className="flex items-center gap-2 text-[13.5px] text-muted-foreground" data-testid="chat-tool-line" aria-live="polite">
    <span className="live-dot" aria-hidden="true" />{label}
  </p>
);

/** «Henta frå kalenderen og prislista» – the grounding, said once, quietly. */
const Sources = ({ steps }) => {
  const nouns = [...new Set((steps || []).map((s) => SOURCE[s]).filter(Boolean))];
  if (!nouns.length) return null;
  const text = nouns.length === 1 ? nouns[0] : `${nouns.slice(0, -1).join(", ")} og ${nouns[nouns.length - 1]}`;
  return (
    <p className="flex items-center gap-1.5 text-[12px] text-muted-foreground" data-testid="chat-steps">
      <Check className="h-3 w-3 text-status-confirmed" aria-hidden="true" />Henta frå {text}
    </p>
  );
};

const Typing = () => (
  <span className="inline-flex items-center gap-1 rounded-[16px] rounded-bl-[6px] bg-secondary px-3.5 py-3" aria-label="Skriv svar" data-testid="chat-typing">
    {[0, 1, 2].map((i) => <motion.span key={i} className="h-1.5 w-1.5 rounded-full bg-foreground/55" animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }} transition={{ duration: 1.1, repeat: Infinity, delay: i * 0.16, ease: "easeInOut" }} />)}
  </span>
);

const Divider = ({ ts }) => (
  <div className="flex items-center gap-3 py-1" data-testid="chat-divider" aria-hidden="true">
    <span className="h-px flex-1 bg-border" />
    <span className="t-mono-sm text-muted-foreground">{dayLabel(ts)} · {timeOf(ts)}</span>
    <span className="h-px flex-1 bg-border" />
  </div>
);

const Welcome = ({ onPick, disabled, wide = false }) => (
  <div className="flex min-h-full flex-col justify-end" data-testid="chat-welcome">
    <ChatMark className="h-11 w-11" />
    <h2 className="mt-5 font-display text-[clamp(24px,2vw,30px)] font-semibold leading-[1.05] tracking-[-0.025em] text-balance">{greeting()}! Kva kan vi <em className="t-swash">hjelpe</em> deg med?</h2>
    <p className="mt-3 max-w-[40ch] text-[15px] leading-relaxed text-muted-foreground text-pretty">Rom, prisar, ledige datoar og program – svara kjem frå husets eigne data, og styret stadfestar alltid til slutt.</p>
    <ul className={cn("mt-6 grid gap-2", wide ? "sm:grid-cols-2 lg:grid-cols-3" : "grid-cols-1 min-[420px]:grid-cols-2")}>
      {QUICK.map((q, i) => (
        <li key={q.label}>
          <button
            type="button"
            onClick={() => onPick(q.text)}
            disabled={disabled}
            className="group flex w-full items-center gap-3 rounded-[14px] bg-card px-3.5 py-3 text-left ring-1 ring-foreground/[0.08] transition-[transform,box-shadow,background-color] duration-200 ease-house hover:-translate-y-0.5 hover:bg-card hover:shadow-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:hover:translate-y-0"
            data-testid={`chat-quick-${i}`}
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-foreground transition-colors group-hover:bg-sol group-hover:text-ink"><q.icon className="h-4 w-4" /></span>
            <span className="min-w-0 flex-1 text-[14px] font-medium leading-tight">{q.label}</span>
          </button>
        </li>
      ))}
    </ul>
  </div>
);

const FollowUps = ({ items, onPick, disabled }) => (
  <div className="flex flex-wrap gap-2" data-testid="chat-followups">
    {items.map((t) => (
      <button key={t} type="button" onClick={() => onPick(t)} disabled={disabled} className="inline-flex h-9 items-center rounded-full border border-foreground/15 bg-background px-3.5 text-[13px] font-medium text-foreground/85 transition-colors hover:border-foreground hover:bg-foreground hover:text-background disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="chat-followup">{t}</button>
    ))}
  </div>
);

/** Copy + thumbs under a finished answer. Ratings stay on the device. */
const Actions = ({ m }) => {
  const [rating, setR] = useState(() => getRating(m.id));
  const copy = async () => {
    try { await navigator.clipboard.writeText(m.content || ""); toast.success("Svaret er kopiert"); } catch { toast.error("Fekk ikkje kopiert"); }
  };
  const rate = (v) => {
    const next = rating === v ? null : v;
    setRating(m.id, next); setR(next);
    if (next) toast.success(next === "up" ? "Takk! Godt å høyre." : "Takk for tilbakemeldinga", { description: next === "down" ? "Prøv gjerne å spørje på ein annan måte – eller bruk skjemaet." : undefined });
  };
  const btn = "flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  return (
    <div className="-ml-1.5 flex items-center gap-0.5 opacity-0 transition-opacity duration-200 group-hover/msg:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100" data-testid="chat-actions">
      <button type="button" onClick={copy} className={btn} aria-label="Kopier svaret" data-testid="chat-copy"><Copy className="h-3.5 w-3.5" /></button>
      <button type="button" onClick={() => rate("up")} className={cn(btn, rating === "up" && "bg-secondary text-foreground")} aria-label="Nyttig svar" aria-pressed={rating === "up"} data-testid="chat-rate-up"><ThumbsUp className={cn("h-3.5 w-3.5", rating === "up" && "fill-current")} /></button>
      <button type="button" onClick={() => rate("down")} className={cn(btn, rating === "down" && "bg-secondary text-foreground")} aria-label="Ikkje nyttig" aria-pressed={rating === "down"} data-testid="chat-rate-down"><ThumbsDown className={cn("h-3.5 w-3.5", rating === "down" && "fill-current")} /></button>
      {m.ts && <span className="ml-1.5 text-[11.5px] tabular text-muted-foreground">{timeOf(m.ts)}</span>}
    </div>
  );
};

const Message = ({ m, drafts, busyDraft, streaming, onSend, onConfirmDraft, onCancelDraft, onRetry, canRetry }) => {
  if (m.role === "user") {
    return (
      <motion.div className="group/msg flex flex-col items-end" data-testid="chat-msg-user" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, ease }}>
        <div className="max-w-[85%] whitespace-pre-wrap rounded-[18px] rounded-br-[6px] bg-ink px-4 py-2.5 text-[15px] leading-[1.5] text-paper">{m.content}</div>
        {m.ts && <span className="mt-1 pr-1 text-[11px] tabular text-muted-foreground opacity-0 transition-opacity group-hover/msg:opacity-100 [@media(hover:none)]:opacity-100">{timeOf(m.ts)}</span>}
      </motion.div>
    );
  }
  const showTyping = m.pending && !m.content && !(m.tools?.length);
  return (
    <div className="group/msg flex gap-3" data-testid={m.error ? "chat-msg-error" : "chat-msg-assistant"}>
      <ChatMark className="mt-0.5 h-7 w-7" />
      <div className="min-w-0 flex-1 space-y-3">
        {m.pending && m.tools?.length > 0 && <ToolLine label={m.tools[m.tools.length - 1]} />}
        {/* Grounded cards first (they arrive while the tools run), then the answer that refers to them. */}
        {m.cards?.map((c, i) => (
          <motion.div key={`${c.type}-${i}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, ease }}>
            <AssistantCard card={c} draftState={c.type === "booking_draft" ? drafts[c.draft_id] : undefined} busy={busyDraft === c.draft_id} disabled={streaming} onSend={onSend} onConfirmDraft={onConfirmDraft} onCancelDraft={onCancelDraft} />
          </motion.div>
        ))}
        {showTyping && <Typing />}
        {m.content && (
          <div className={cn("rounded-[18px] rounded-tl-[6px] bg-secondary/70 px-4 py-3", m.error && "bg-transparent px-0 py-0")}>
            <RichText text={m.content} className={cn(m.error && "text-muted-foreground")} />
            {m.pending && <span className="ml-0.5 inline-block h-[1em] w-[2px] translate-y-[2px] animate-pulse bg-foreground/60 align-baseline" aria-hidden="true" />}
          </div>
        )}
        {!m.pending && !m.error && <Sources steps={m.steps} />}
        {!m.pending && !m.error && m.content && <Actions m={m} />}
        {m.error && !m.pending && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[13.5px]">
            {canRetry && <button type="button" onClick={onRetry} className="inline-flex items-center gap-1.5 font-medium u-link" data-testid="chat-retry"><RotateCcw className="h-3.5 w-3.5" />Prøv igjen</button>}
            <Link to="/leig-rom" className="font-medium u-link">Bruk skjemaet</Link>
            <Link to="/kontakt" className="font-medium u-link">Kontakt styret</Link>
          </div>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------------------- chat */
/**
 * Full chat surface: header · transcript · composer. `variant` = "panel" (floating) or "page".
 * `queued` is a message handed over from elsewhere; it is sent once the chat is ready.
 */
export const Chat = ({ active = true, onClose, variant = "panel", className, queued, onQueuedSent }) => {
  const h = useChat({ active });
  const [text, setText] = useState("");
  const [atBottom, setAtBottom] = useState(true);
  const [unseen, setUnseen] = useState(false);
  const listRef = useRef(null);
  const taRef = useRef(null);
  const stickRef = useRef(true);

  useEffect(() => {
    if (!queued || !h.loaded || h.streaming) return;
    stickRef.current = true;
    h.send(queued);
    onQueuedSent?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queued, h.loaded]);

  // keep the newest message in view unless the visitor scrolled up to read
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;
    if (stickRef.current) el.scrollTop = el.scrollHeight;
    else setUnseen(true);
  }, [h.messages, h.loaded]);
  const onScroll = () => {
    const el = listRef.current;
    if (!el) return;
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    stickRef.current = near;
    setAtBottom(near);
    if (near) setUnseen(false);
  };
  const jump = () => { const el = listRef.current; if (el) { stickRef.current = true; el.scrollTo({ top: el.scrollHeight, behavior: "smooth" }); setUnseen(false); } };

  useEffect(() => { if (active && !window.matchMedia("(max-width: 639px)").matches) setTimeout(() => taRef.current?.focus({ preventScroll: true }), 80); }, [active]);

  const autosize = () => { const ta = taRef.current; if (!ta) return; ta.style.height = "0px"; ta.style.height = `${Math.min(ta.scrollHeight, 140)}px`; };
  useEffect(autosize, [text]);

  const sendText = (s) => { stickRef.current = true; h.send(s); };
  const submit = (e) => {
    e?.preventDefault?.();
    if (!text.trim() || h.streaming) return;
    sendText(text);
    setText("");
  };
  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent?.isComposing) { e.preventDefault(); submit(); } };

  const live = h.status?.live;
  const statusText = h.status ? (live ? "Svar frå husets eigne data" : "Demo – faste svar frå husets data") : "Koplar til …";
  const last = h.messages[h.messages.length - 1];
  const followUps = useMemo(() => (last && last.role === "assistant" && !last.pending && !last.error ? followUpsFor(last, h.drafts) : []), [last, h.drafts]);

  return (
    <div className={cn("flex h-full min-h-0 flex-col", className)} data-testid="chat">
      <header className="flex items-center gap-3 border-b border-border/80 px-4 py-3 sm:px-5">
        <ChatMark className="h-9 w-9" />
        <div className="min-w-0 flex-1">
          <p className="font-display text-[17px] font-semibold leading-none tracking-[-0.01em]">Spør oss</p>
          <p className="mt-1.5 flex items-center gap-1.5 text-[12px] text-muted-foreground" data-testid="chat-status"><span className={cn("h-1.5 w-1.5 rounded-full", !h.status ? "bg-border" : live ? "bg-status-confirmed" : "bg-status-pending")} aria-hidden="true" />{statusText}</p>
        </div>
        <TooltipProvider delayDuration={200}>
          <div className="flex items-center gap-0.5">
            {h.messages.length > 0 && <IconButton label="Start på nytt" onClick={h.reset} testId="chat-reset"><RotateCcw className="h-4 w-4" /></IconButton>}
            {variant === "panel" && <IconButton label="Opne som eiga side" to={CHAT_PATH} onClick={onClose} testId="chat-open-page" hiddenOnMobile><Maximize2 className="h-4 w-4" /></IconButton>}
            {onClose && <IconButton label="Lukk" onClick={onClose} testId="chat-close" className="text-foreground"><X className="h-4 w-4" /></IconButton>}
          </div>
        </TooltipProvider>
      </header>

      <div className="relative min-h-0 flex-1">
        <div ref={listRef} onScroll={onScroll} className="h-full overflow-y-auto overscroll-contain px-4 py-5 sm:px-5" data-testid="chat-transcript">
          {!h.loaded ? (
            <div className="space-y-3" data-testid="chat-loading"><Skeleton className="h-11 w-11 rounded-full" /><Skeleton className="h-7 w-2/3" /><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-5/6" /><div className="grid grid-cols-2 gap-2 pt-3"><Skeleton className="h-14" /><Skeleton className="h-14" /><Skeleton className="h-14" /><Skeleton className="h-14" /></div></div>
          ) : h.messages.length === 0 ? (
            <Welcome onPick={sendText} disabled={h.streaming} wide={variant === "page"} />
          ) : (
            <div className="space-y-5" aria-live="polite">
              {h.messages.map((m, i) => (
                <div key={m.id || i} className="space-y-5">
                  {needsDivider(h.messages[i - 1], m) && <Divider ts={m.ts} />}
                  <Message m={m} drafts={h.drafts} busyDraft={h.busyDraft} streaming={h.streaming} onSend={sendText} onConfirmDraft={h.confirmDraft} onCancelDraft={h.cancelDraft} onRetry={h.retry} canRetry={Boolean(h.lastFailed) && i === h.messages.length - 1} />
                </div>
              ))}
              {followUps.length > 0 && !h.streaming && <div className="pl-10"><FollowUps items={followUps} onPick={sendText} disabled={h.streaming} /></div>}
            </div>
          )}
        </div>
        <AnimatePresence>
          {!atBottom && (unseen || h.streaming) && (
            <motion.button
              key="jump"
              type="button"
              onClick={jump}
              className="absolute bottom-3 left-1/2 flex h-9 -translate-x-1/2 items-center gap-1.5 rounded-full border border-border bg-background/90 px-3.5 text-[13px] font-medium shadow-soft backdrop-blur-md transition-colors hover:bg-foreground hover:text-background"
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }} transition={{ duration: 0.18 }}
              data-testid="chat-jump"
            >
              <ArrowDown className="h-3.5 w-3.5" />{h.streaming ? "Svaret kjem" : "Nytt svar"}
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      <form onSubmit={submit} className="safe-bottom border-t border-border/80 px-3 pt-3 sm:px-4" data-testid="chat-composer">
        <div className="flex items-end gap-2 rounded-[22px] bg-card px-3.5 py-2 ring-1 ring-foreground/10 transition-shadow duration-200 focus-within:ring-2 focus-within:ring-foreground/60">
          <textarea
            ref={taRef}
            rows={1}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKey}
            maxLength={1500}
            placeholder="Skriv spørsmålet ditt …"
            aria-label="Skriv spørsmålet ditt"
            className="max-h-[140px] min-h-[28px] flex-1 resize-none bg-transparent py-1.5 text-[15px] leading-[1.5] outline-none placeholder:text-muted-foreground"
            data-testid="chat-input"
          />
          {h.streaming ? (
            <button type="button" onClick={h.stop} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-foreground transition-colors hover:bg-border" aria-label="Stopp svaret" data-testid="chat-stop"><Square className="h-3.5 w-3.5 fill-current" /></button>
          ) : (
            <button type="submit" disabled={!text.trim()} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ink text-paper transition-[opacity,transform,background-color] duration-200 hover:scale-105 hover:bg-sol hover:text-ink disabled:opacity-25 disabled:hover:scale-100 disabled:hover:bg-ink disabled:hover:text-paper" aria-label="Send" data-testid="chat-send"><ArrowUp className="h-4 w-4" /></button>
          )}
        </div>
        <p className="px-1 pb-3 pt-2 text-[11.5px] leading-snug text-muted-foreground">Rettleiande svar frå prislista og kalenderen – styret stadfestar alltid. <Link to="/leig-rom" className="u-link" onClick={onClose}>Skjema</Link> · <Link to="/kontakt" className="u-link" onClick={onClose}>Kontakt</Link></p>
      </form>
    </div>
  );
};

/* --------------------------------------------------------------------------- launcher */
const HIDE_ON = [CHAT_PATH, "/husvert", "/admin", "/arrangor", "/billett"];
const isHiddenPath = (pathname) => HIDE_ON.some((p) => pathname.startsWith(p));

const Mark = ({ className }) => (
  <svg viewBox="0 0 32 32" className={className} fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden="true">
    <path d="M6 22a10 10 0 0 1 20 0" opacity=".45" />
    <path d="M10 22a6 6 0 0 1 12 0" opacity=".75" />
    <path d="M13.5 22a2.5 2.5 0 0 1 5 0" />
    <circle cx="16" cy="22" r="1.6" fill="hsl(var(--sol))" stroke="none" />
  </svg>
);

/** Phones and tablets (< lg): the way into the chat lives in the sticky top bar, in reserved space, so nothing
 *  floats over the content or the links underneath. The desktop keeps the round floating launcher. */
export const ChatHeaderButton = () => {
  const { pathname } = useLocation();
  if (isHiddenPath(pathname)) return null;
  return (
    <button type="button" onClick={() => window.dispatchEvent(new CustomEvent(CHAT_OPEN_EVENT, { detail: {} }))} className="flex h-10 w-10 items-center justify-center rounded-full text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground sm:h-11 sm:w-11 lg:hidden" aria-label="Spør oss – chat om rom, prisar og ledige datoar" title="Spør oss" data-testid="chat-header-button">
      <Mark className="h-7 w-7" />
    </button>
  );
};

/** Floating entry point on public pages: a round mark that opens the chat as a glass panel (full screen on phones). */
export const ChatLauncher = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [queued, setQueued] = useState(null);
  const [hello, setHello] = useState(false);
  const reduce = useReducedMotion();

  // open-from-anywhere
  useEffect(() => {
    const onOpen = (e) => { setQueued(e.detail?.message || null); setOpen(true); };
    window.addEventListener(CHAT_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(CHAT_OPEN_EVENT, onOpen);
  }, []);

  // a small hello next to the button, once per device
  useEffect(() => {
    if (open || wasGreeted()) return undefined;
    const show = setTimeout(() => setHello(true), 2800);
    const hide = setTimeout(() => { setHello(false); markGreeted(); }, 2800 + 9000);
    return () => { clearTimeout(show); clearTimeout(hide); };
  }, [open]);
  const dismissHello = () => { setHello(false); markGreeted(); };

  useEffect(() => {
    if (!open) return undefined;
    setHello(false); markGreeted();
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    const mobile = window.matchMedia("(max-width: 639px)").matches;
    const prev = document.body.style.overflow;
    if (mobile) document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open]);

  const hidden = isHiddenPath(pathname);
  if (hidden) return null;
  const liftForBar = pathname === "/arranger"; // the organiser page has its own mobile bottom bar

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            key="chat-panel"
            role="dialog"
            aria-label="Spør oss – chat med kulturhuset"
            className="fixed inset-0 z-[90] flex flex-col bg-background sm:inset-auto sm:bottom-5 sm:right-5 sm:h-[min(680px,calc(100dvh-40px))] sm:w-[400px] sm:origin-bottom-right sm:overflow-hidden sm:rounded-[22px] sm:bg-background/95 sm:shadow-lift sm:ring-1 sm:ring-foreground/10 sm:backdrop-blur-xl"
            initial={reduce ? false : { opacity: 0, y: 16, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: 12, scale: 0.97 }}
            transition={{ duration: 0.24, ease }}
            data-testid="chat-panel"
          >
            <Chat active={open} onClose={() => setOpen(false)} variant="panel" queued={queued} onQueuedSent={() => setQueued(null)} />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {!open && hello && (
          <motion.div
            key="chat-hello"
            className={cn("fixed right-[76px] z-[89] hidden lg:block sm:right-[84px]", liftForBar ? "bottom-[88px] lg:bottom-[25px]" : "bottom-[20px] sm:bottom-[25px]")}
            initial={reduce ? false : { opacity: 0, x: 8, scale: 0.96 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 6, scale: 0.97 }}
            transition={{ duration: 0.22, ease }}
            data-testid="chat-greeting"
          >
            <div className="relative flex items-center gap-1 rounded-[14px] bg-card py-1.5 pl-4 pr-1.5 text-[14px] shadow-lift ring-1 ring-foreground/10">
              <button type="button" onClick={() => setOpen(true)} className="py-1 pr-1 text-left font-medium text-foreground focus-visible:outline-none" data-testid="chat-greeting-open">Lurer du på noko? <span className="text-muted-foreground">Spør oss.</span></button>
              <button type="button" onClick={dismissHello} className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground" aria-label="Lukk" data-testid="chat-greeting-close"><X className="h-3.5 w-3.5" /></button>
              <span className="absolute -right-1.5 top-1/2 h-3 w-3 -translate-y-1/2 rotate-45 bg-card ring-1 ring-foreground/10 [clip-path:polygon(100%_0,100%_100%,0_100%)]" aria-hidden="true" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {!open && (
          <motion.button
            key="chat-launcher"
            type="button"
            onClick={() => setOpen(true)}
            className={cn(
              "group fixed right-4 z-[89] hidden h-14 w-14 items-center justify-center rounded-full bg-ink text-paper shadow-lift transition-[transform,background-color] duration-200 ease-house hover:scale-105 hover:bg-ink/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:right-5 lg:flex",
              liftForBar ? "bottom-[84px] lg:bottom-5" : "bottom-4 sm:bottom-5"
            )}
            initial={reduce ? false : { opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.2, ease }}
            aria-label="Spør oss – chat om rom, prisar og ledige datoar"
            title="Spør oss"
            data-testid="chat-launcher"
          >
            <Mark className="h-7 w-7" />
            {!reduce && <span className="pointer-events-none absolute inset-0 rounded-full ring-2 ring-sol/60 opacity-0 transition-opacity duration-300 group-hover:opacity-100" aria-hidden="true" />}
            <span className="sr-only">Spør oss</span>
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
};
